import { logError } from '@savgroup-front-common/configuration/appInsights/AppInsights';

import { globalAuthConfiguration, userManager } from '../userManager';

import {
  REVERS_TRANSLATE_RESOURCE,
  REVERS_TRANSLATE_SCOPE,
} from './dynamicTermSuggestion.configuration';
import { ReversTranslateToken } from './dynamicTermSuggestion.types';

const EXPIRATION_MARGIN_IN_MILLISECONDS = 60 * 1000;

const STRUCTURAL_GRANT_ERRORS = [
  'invalid_scope',
  'invalid_target',
  'invalid_client',
  'unauthorized_client',
  'unsupported_grant_type',
];

let cachedAccessToken: string | undefined;
let cachedAccessTokenExpiresAt = 0;
let isGrantBlocked = false;

const readGrantError = async (
  response: Response,
): Promise<{ error?: string; errorDescription?: string }> => {
  try {
    const { error, error_description: errorDescription } =
      await response.json();

    return { error, errorDescription };
  } catch {
    return {};
  }
};

const decodeAccessTokenExpiration = (accessToken: string): number => {
  try {
    const [, payload] = accessToken.split('.');
    const { exp } = JSON.parse(
      atob(payload.replace(/-/g, '+').replace(/_/g, '/')),
    );

    return typeof exp === 'number' ? exp * 1000 : 0;
  } catch {
    return 0;
  }
};

const persistRotatedRefreshToken = async ({
  rotatedRefreshToken,
}: {
  rotatedRefreshToken?: string;
}): Promise<void> => {
  if (!rotatedRefreshToken) {
    return;
  }

  const currentUser = await userManager?.getUser();

  if (!currentUser || currentUser.refresh_token === rotatedRefreshToken) {
    return;
  }

  currentUser.refresh_token = rotatedRefreshToken;

  await userManager.storeUser(currentUser);
};

export const invalidateReversTranslateToken = (): void => {
  cachedAccessToken = undefined;
  cachedAccessTokenExpiresAt = 0;
};

export const resetReversTranslateTokenForTests = (): void => {
  invalidateReversTranslateToken();
  isGrantBlocked = false;
};

export const getReversTranslateToken = async (): Promise<
  ReversTranslateToken | undefined
> => {
  if (
    isGrantBlocked ||
    !REVERS_TRANSLATE_RESOURCE ||
    !globalAuthConfiguration?.authority ||
    !globalAuthConfiguration?.clientId
  ) {
    return undefined;
  }

  const user = await userManager?.getUser();

  if (!user?.refresh_token || !user?.id_token) {
    return undefined;
  }

  if (cachedAccessToken && Date.now() < cachedAccessTokenExpiresAt) {
    return { accessToken: cachedAccessToken, idToken: user.id_token };
  }

  try {
    const response = await fetch(`${globalAuthConfiguration.authority}/token`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/x-www-form-urlencoded',
      },
      body: new URLSearchParams({
        grant_type: 'refresh_token',
        refresh_token: user.refresh_token,
        client_id: globalAuthConfiguration.clientId,
        resource: REVERS_TRANSLATE_RESOURCE,
        scope: REVERS_TRANSLATE_SCOPE,
      }),
    });

    if (!response.ok) {
      const { error, errorDescription } = await readGrantError(response);

      isGrantBlocked = !!error && STRUCTURAL_GRANT_ERRORS.includes(error);

      logError(new Error('Revers Translate token grant failed'), {
        operation: 'Get Revers Translate token',
        status: response.status,
        error,
        errorDescription,
        resource: REVERS_TRANSLATE_RESOURCE,
        scope: REVERS_TRANSLATE_SCOPE,
        isGrantBlocked,
      });

      return undefined;
    }

    const { access_token: accessToken, refresh_token: rotatedRefreshToken } =
      await response.json();

    await persistRotatedRefreshToken({ rotatedRefreshToken });

    if (!accessToken) {
      return undefined;
    }

    cachedAccessToken = accessToken;
    cachedAccessTokenExpiresAt =
      decodeAccessTokenExpiration(accessToken) -
      EXPIRATION_MARGIN_IN_MILLISECONDS;

    return { accessToken, idToken: user.id_token };
  } catch (error) {
    logError(error, {
      operation: 'Get Revers Translate token',
    });

    return undefined;
  }
};
